import { useFirebaseAuth } from "../hooks/use-firebase-auth";
import { Loader2 } from "lucide-react";
import { Redirect, Route } from "wouter";
import Layout from "@/components/layout/layout";

function authRedirectWithReturnTo(fallbackPath: string): string {
  const returnTarget =
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}`
      : fallbackPath;
  return `/auth?returnTo=${encodeURIComponent(returnTarget)}`;
}

// Direct implementation of protected route using useAuth hook
export function ProtectedRoute({
  path,
  component: Component,
}: {
  path: string;
  component: () => React.JSX.Element;
}) {
  return (
    <Route path={path}>
      {(params) => <ProtectedContent Component={Component} params={params} />}
    </Route>
  );
}

// Separate component to handle the auth logic inside the route
function ProtectedContent({
  Component,
  params,
}: {
  Component: () => React.JSX.Element;
  params: any;
}) {
  // Use the safe try/catch version of useAuth to handle cases where
  // AuthContext might not be available yet
  let user = null;
  let isLoading = true;
  let error = null;

  try {
    // This will throw an error if the context is not available
    const auth = useFirebaseAuth();
    user = auth.user;
    isLoading = auth.isLoading;
    error = auth.error;
  } catch (e) {
    // If useFirebaseAuth throws, we're not in the FirebaseAuthProvider context yet
    console.log(
      "Firebase auth context not available in ProtectedRoute, redirecting to login"
    );
    return <Redirect to={authRedirectWithReturnTo("/dashboard")} />;
  }

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[50vh] bg-dark">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-white">Verifying authentication...</span>
        </div>
      </Layout>
    );
  }

  // If user is not authenticated, redirect to login
  if (!user) {
    return <Redirect to={authRedirectWithReturnTo("/dashboard")} />;
  }

  // If we have a user, render the protected component
  return <Component />;
}
